/*
Toggle class on another element.

HTML Usage:
<toggle-element-class data-options='{}'>
  <button tabindex="0">
    Toggle element class button
  </button>
</toggle-element-class>
*/

if (!customElements.get('toggle-element-class')) {
  class ToggleElementClass extends HTMLElement {
    constructor() {
      super();

      // Options
      this.options = {
        toggleClass: 'toggle--is-active',
        toggleTriggerClass: '',
        target: 'body',
        onHover: false,
        hoverDelay: 0,
        preventDefault: true,
        stopPropagation: true,
        ariaExpanded: false,
      };

      // Get options from element data and combine with this.options
      if (this?.dataset?.options) {
        const dataOptions = JSON.parse(this.dataset.options);
        this.options = {
          ...this.options,
          ...dataOptions,
        };
      }

      // For the menu drawer, filters, cart drawer and search, define the content they expand
      if (this.options.toggleClass === 'menu-drawer-is-open') this.targetID = 'menuDrawer';
      if (this.options.toggleClass === 'cart-drawer-is-open') this.targetID = 'cartDrawer';
      if (this.options.toggleClass === 'filter-is-open') this.targetID = 'CollectionFiltersForm';
      if (this.options.toggleClass === 'header-search-is-open') this.targetID = 'SearchModal';

      // Reduce actions (So the event can also be removed)
      this.reducer = {
        click: (e) => this.debounceClickEvent(e),
        mouseEnter: (e) => this.debouncedOnMouse(e, 'add'),
        mouseLeave: (e) => this.debouncedOnMouse(e, 'remove'),
      };

      // Construct web component
      this.construct();
    }

    /*
      Construct web component
    */
    construct() {
      this.target = document.querySelector(this.options.target);

      if (!this.target) {
        return false;
      }

      // Click
      this.removeEventListener('click', this.reducer.click);
      this.addEventListener('click', this.reducer.click);

      // Mouse enter and leave
      if (this.options.onHover) {
        this.removeEventListener('mouseenter', this.reducer.mouseEnter);
        this.addEventListener('mouseleave', this.reducer.mouseLeave);
      }
    }

    /*
      Debounce click event
      @param e {object}: click event
      Called by add event listener -> reducer.

      The exceptions for the menu drawer are to allow keyboard accessibility in the menu drawer as wel as the check to click on 'more items' in the menu
    */
    debounceClickEvent(e) {
      if (
        !e ||
        (e.target.closest('.menu-drawer') && !e.target.closest('[data-header-link-group-toggle]'))
      ) {
        return false;
      }

      this.preventDefault(e);
      this.stopPropagation(e);
      this.toggle();
    }

    /*
      Debounced mouse event
      @param e {object}: mouse event
      @param type {string}: open/close/closeAll
      Called by add event listener -> reducer.
    */
    debouncedOnMouse(e, type) {
      if (!e) {
        return false;
      }

      const debouncer = debounce((type) => {
        switch (type) {
        case 'add':
          this.add();
          break;
        case 'remove':
          this.remove();
          break;
        default:
          this.toggle();
          break;
        }
      }, this.options.hoverDelay);
      debouncer(type);
    }

    // @param e {object}: click event
    preventDefault(e) {
      if (this.options.preventDefault) {
        e.preventDefault();
      }
    }

    // @param e {object}: click event
    stopPropagation(e) {
      if (this.options.stopPropagation) {
        e.stopPropagation();
      }
    }

    keyUpCloseEvent(event) {
      if (event?.code?.toUpperCase() !== 'ESCAPE') return;
      this.remove();
      removeTrapFocus();
    }

    add() {
      const { toggleClass, toggleTriggerClass } = this.options;
      if (this.target) {
        this.target.classList.add(toggleClass);
      }
      if (toggleTriggerClass) {
        this.classList.add(toggleTriggerClass);
      }
      // Switch aria-expanded
      if (this.options.ariaExpanded) {
        if (this.children[0].nodeName === 'BUTTON') {
          this.children[0].setAttribute('aria-expanded', true);
        } else {
          this.setAttribute('aria-expanded', true);
        }
      }

      // when this is used to show more items, focus the first newly visible item (used for items in the menu and the filters)
      if ('headerLinkGroupToggle' in this.dataset) {
        trapFocus(
          this.closest('[data-collapsible-group]'),
          this.closest('[data-collapsible-group]'),
          true,
        );
      }
      if ('filterGroupToggle' in this.dataset) {
        this.target.querySelectorAll('.hidden > label')[0].focus();
      }

      // for the cart drawer, trap the focus and prevent scroll
      if (toggleClass === 'cart-drawer-is-open') {
        if (document.body.classList.contains('template-cart')) return;

        document.querySelector('cart-drawer').querySelector('[data-cart-drawer]').scrollTop = 0;
      }
      // for the search, close the other open elements and focus the input
      if (toggleClass === 'header-search-is-open') {
        document.body.classList.remove('header--open');
        document.body.classList.remove('menu-drawer-is-open');

        // Focus on input element if provided
        if (this.options.focusTarget) {
          document.querySelector(this.options.focusTarget)?.focus();
        }
      }

      // listen for key up event
      if (
        toggleClass === 'menu-drawer-is-open' ||
        toggleClass === 'filter-is-open' ||
        toggleClass === 'header-search-is-open' ||
        toggleClass === 'cart-drawer-is-open'
      ) {
        document.addEventListener('keyup', this.keyUpCloseEvent.bind(this));
        document
          .querySelectorAll(`button[aria-controls="${this.targetID}"]`)
          ?.forEach((button) => button.setAttribute('aria-expanded', true));

        showModal(document.getElementById(this.targetID));
        trapFocus(document.getElementById(this.targetID));
      }
    }

    remove() {
      const { toggleClass, toggleTriggerClass } = this.options;
      if (this.target) {
        this.target.classList.remove(toggleClass);
      }
      if (toggleTriggerClass) {
        this.classList.remove(toggleTriggerClass);
      }
      // Switch aria-expanded
      if (this.options.ariaExpanded) {
        if (this.children[0].nodeName === 'BUTTON') {
          this.children[0].setAttribute('aria-expanded', false);
        } else {
          this.setAttribute('aria-expanded', false);
        }
      }

      // remove the modal class
      if (toggleClass === 'header--open') {
        if (document.documentElement.classList.contains('touch'))
          hideModal(document.getElementById('MainMenu'));
      }

      // hide filter items again
      if (document.querySelectorAll('[data-filter-group]')) {
        document
          .querySelectorAll('[data-filter-group]')
          .forEach((filter) => filter.classList.remove('show-hidden-links'));
      }

      if (
        toggleClass === 'menu-drawer-is-open' ||
        toggleClass === 'filter-is-open' ||
        toggleClass === 'header-search-is-open' ||
        toggleClass === 'cart-drawer-is-open'
      ) {
        hideModal(document.getElementById(this.targetID), false);
        removeTrapFocus();

        const predictiveSearch = document.querySelector('predictive-search');
        if (predictiveSearch && toggleClass === 'header-search-is-open') {
          predictiveSearch.close();
        }

        document
          .querySelectorAll(`button[aria-controls="${this.targetID}"]`)
          ?.forEach((button) => button.setAttribute('aria-expanded', false));
        document.removeEventListener('keyup', this.keyUpCloseEvent.bind(this));

        // return the focus to the trigger
        this.querySelector('button')?.focus();
      }
    }

    toggle() {
      const { toggleClass } = this.options;
      if (!this.target) {
        return false;
      }
      if (!this.target.classList.contains(toggleClass)) {
        this.add();
      } else {
        this.remove();
      }
    }
  }

  window.ToggleElementClass = ToggleElementClass;

  customElements.define('toggle-element-class', ToggleElementClass);
}
